import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import { ClientSettings, Organization} from '@ee/common/models';
import {Subscription} from 'rxjs';
import {ClientService} from '@ee/common/services';

@Component({
  selector: 'ee-client-select',
  template: `
    <div class="client-select-wrapper flex flex-col justify-stretch items-stretch">
      <mat-form-field class="compact mb-4">
        <mat-label>Search</mat-label>
        <input matInput [(ngModel)]="query" autocomplete="off" [disabled]="disabled">
      </mat-form-field>
      @if (filteredClients?.length) {
        <div #listWrapper class="list-wrapper flex flex-row flex-[1_0_100%]">
          <mat-list class="searchable-list w-full">
            @for (client of filteredClients | search:'company_name,identifier':query; track client) {
              <mat-list-item
                (click)="!disabled ? onSelectionChanged(client) : null" class="client-row"
                [ngClass]="{'selected': client === selectedClient, 'disabled': disabled}">
                <div class="flex flex-row justify-stretch items-center">
                  <div class="primary">{{client.company_name}}</div>
                  <div class="secondary">{{client.identifier}}</div>
                </div>
              </mat-list-item>
            }
          </mat-list>
        </div>
      } @else {
        <div class="flex flex-row flex-[1_0_100%] justify-center items-center">
          No clients exist. At least one client must exist to continue.
        </div>
      }
    </div>
    `,
  styles: [`
    @use 'components/color-palette';

    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: hidden;
      min-height: 100%;
    }

    .client-select-wrapper {
      overflow-y: hidden;
    }

    .list-wrapper {
      max-height: 200px;
      overflow: auto;
      border: 1px solid var(--c-gray-90);
    }

    .primary {
      flex: 1;
      overflow: hidden;
      white-space: nowrap;
      display: inline-block;
      vertical-align: top;
      text-overflow: ellipsis;
    }
  `],
  standalone: false
})
export class ClientSelectComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  filteredClients: Organization[];
  query: string;
  subs: Subscription[] = [];

  @ViewChild('listWrapper', { static: false }) listWrapper: ElementRef<HTMLElement>;

  @Input() autoSelect = true;
  @Input() initClientId: string;
  @Input() selectedClient: Organization;
  @Input() fields: string;
  @Input() disabled = false;
  @Output() selectedClientChange: EventEmitter<Organization> = new EventEmitter<Organization>();
  @Output() clientCount: EventEmitter<number> = new EventEmitter<number>();

  constructor(private clientService: ClientService, private cdr: ChangeDetectorRef) {
  }

  ngOnInit() {
    const fields = ['company_name', 'identifier', 'county_override_rules'];
    if (this.fields?.length) {
      this.fields.split(',').forEach((f) => {
        if (!fields.includes(f)) {
          fields.push(f);
        }
      });
    }
    this.subs.push(this.clientService.searchClientsByCompanyName('', fields)
      .subscribe((clients: Organization[]) => {
        this.clientCount.emit(clients.length);
        this.filteredClients = clients;
        if (this.filteredClients.length && this.autoSelect) {
          if (!this.initClientId || !this.selectInitClient()) {
            this.selectedClient = this.filteredClients[0];
            this.selectedClientChange.emit(this.selectedClient);
          }
          // scroll will be handled after selection completes and view is ready
        }
      }));
  }

  ngAfterViewInit() {
    if (this.selectedClient) {
      this.scrollToSelected();
    }
  }

  ngOnChanges() {
    if (this.initClientId && this.filteredClients) {
      this.selectInitClient();
      this.scrollToSelected();
    }
  }

  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
  }

  private scrollToSelected() {
    // Use requestAnimationFrame to ensure DOM is updated
    requestAnimationFrame(() => {
      setTimeout(() => {
        const selectedClientElement = this.listWrapper?.nativeElement?.querySelector('.client-row.selected');
        if (selectedClientElement) {
          selectedClientElement.scrollIntoView({block: 'nearest', inline: 'nearest', behavior: 'smooth'});
        }
      }, 100);
    });
  }

  onSelectionChanged(client: Organization) {
    if (!client?.id) {
      return;
    }

    this.subs.push(this.clientService.getClientSettings(client.id).subscribe({
      next: (settings: ClientSettings) => {
        client.client_settings = settings;
        this.selectedClient = client;
        this.selectedClientChange.emit(client);
        this.scrollToSelected();
      },
      error: () => {
        // If settings retrieval fails, still update selection and emit change
        this.selectedClient = client;
        this.selectedClientChange.emit(client);
        this.scrollToSelected();
      }
    }));
  }

  private selectInitClient(): boolean {
    const result = this.filteredClients.filter(c => c.id === this.initClientId);
    if (result.length) {
      this.onSelectionChanged(result[0]);
      return true;
    }
    return false;
  }

}
